import type { MonthlyReportData } from '@elva/ui';
import type { MrChannel, MrSeoStat } from '@elva/ui';
import { growthGet, growthGetAccepted, growthPostAsync, growthPostBlob } from '@/data/http';
import { downloadBlob } from '@/data/download';
import type { BeMonthlyReport, MarketingJobData } from '@/data/marketingApiTypes';
import { centsToUsd, num, str } from '@/data/adapt';

export interface MonthlyReportMonth {
  label: string;
  value: string;
  status: 'month_to_date' | 'finalized';
}

export interface MonthlyReportFetchResult {
  data: MonthlyReportData;
  generating: boolean;
  reportId?: string;
  jobId?: string;
}

type BeGoogleAdsBreakdown = {
  impressions?: number;
  clicks?: number;
  spend_cents?: number;
  leads?: number;
  attributed_revenue_cents?: number;
};

type BeSeoBreakdown = {
  articles_created?: number;
  articles_published?: number;
  rankings_count?: number;
  avg_rank?: number;
};

type BeGbpBreakdown = {
  photos_uploaded?: number;
  posts_created?: number;
  qa_items?: number;
  review_responses?: number;
};

type BeAiReceptionistBreakdown = {
  calls?: number;
  total_duration_seconds?: number;
};

type BeChannelBreakdown = {
  google_ads?: BeGoogleAdsBreakdown;
  seo?: BeSeoBreakdown;
  gbp?: BeGbpBreakdown;
  ai_receptionist?: BeAiReceptionistBreakdown;
};

function mapMonthStatus(status: string | undefined): 'month_to_date' | 'finalized' {
  const key = (status ?? '').toLowerCase().replace(/-/g, '_');
  if (key === 'ready' || key === 'finalized') return 'finalized';
  return 'month_to_date';
}

function centsToDollars(cents: number | undefined): number {
  return num(cents) / 100;
}

function formatCount(value: number | undefined): string {
  return num(value).toLocaleString();
}

function formatDuration(seconds: number | undefined): string {
  const total = num(seconds);
  if (total <= 0) return '0m';
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function hasHeroValues(hero: Record<string, unknown> | undefined): boolean {
  if (!hero) return false;
  return (
    num(hero.ad_spend ?? hero.adSpend) > 0 ||
    num(hero.revenue) > 0 ||
    num(hero.new_patients ?? hero.newPatients) > 0
  );
}

function buildHeroFromGoogleAds(ads: BeGoogleAdsBreakdown | undefined): MonthlyReportData['hero'] {
  if (!ads) return undefined;
  const adSpend = centsToDollars(ads.spend_cents);
  const revenue = centsToDollars(ads.attributed_revenue_cents);
  const newPatients = num(ads.leads);
  const roi = adSpend > 0 ? Math.round((revenue / adSpend) * 10) / 10 : 0;
  return {
    adSpend: Math.round(adSpend),
    revenue: Math.round(revenue),
    roi,
    newPatients,
    lastMonth: { adSpend: 0, revenue: 1, roi: 1, newPatients: 0 },
  };
}

function buildChannelsFromBreakdown(breakdown: BeChannelBreakdown): MrChannel[] {
  const channels: MrChannel[] = [];

  const ads = breakdown.google_ads;
  if (ads) {
    const spend = centsToDollars(ads.spend_cents);
    const revenue = centsToDollars(ads.attributed_revenue_cents);
    const impressions = num(ads.impressions);
    const clicks = num(ads.clicks);
    const ctr = impressions > 0 ? `${((clicks / impressions) * 100).toFixed(1)}%` : '—';
    channels.push({
      id: 'google_ads',
      name: 'Google Ads',
      tone: 'info',
      icon: 'ads',
      summary:
        spend > 0 || revenue > 0
          ? `${centsToUsd(ads.attributed_revenue_cents)} revenue on ${centsToUsd(ads.spend_cents)} spend · ${formatCount(ads.leads)} leads`
          : 'Performance data pending',
      metrics: [
        { label: 'Ad Spend', value: centsToUsd(ads.spend_cents) },
        { label: 'Revenue', value: centsToUsd(ads.attributed_revenue_cents), tone: 'success' },
        { label: 'Impressions', value: formatCount(ads.impressions) },
        { label: 'Clicks', value: formatCount(ads.clicks) },
        { label: 'Leads', value: formatCount(ads.leads) },
        { label: 'CTR', value: ctr },
      ],
    });
  }

  const seo = breakdown.seo;
  if (seo) {
    channels.push({
      id: 'seo',
      name: 'Organic SEO',
      tone: 'success',
      icon: 'seo',
      summary: `${formatCount(seo.articles_published)} articles published · ${formatCount(seo.rankings_count)} rankings tracked`,
      metrics: [
        { label: 'Articles Created', value: formatCount(seo.articles_created) },
        { label: 'Articles Published', value: formatCount(seo.articles_published) },
        { label: 'Keywords Tracked', value: formatCount(seo.rankings_count) },
        { label: 'Avg Rank', value: seo.avg_rank != null ? String(seo.avg_rank) : '—' },
      ],
      note: 'Organic revenue attribution is indirect — SEO builds long-term visibility that supports your paid pipeline.',
    });
  }

  const gbp = breakdown.gbp;
  if (gbp) {
    channels.push({
      id: 'gbp',
      name: 'Google Business Profile',
      tone: 'gold',
      icon: 'gbp',
      summary: `${formatCount(gbp.posts_created)} posts · ${formatCount(gbp.review_responses)} review responses · ${formatCount(gbp.photos_uploaded)} photos`,
      metrics: [
        { label: 'Photos Uploaded', value: formatCount(gbp.photos_uploaded) },
        { label: 'Posts Created', value: formatCount(gbp.posts_created) },
        { label: 'Q&A Items', value: formatCount(gbp.qa_items) },
        { label: 'Review Responses', value: formatCount(gbp.review_responses) },
      ],
    });
  }

  return channels;
}

function buildActivitySections(breakdown: BeChannelBreakdown): NonNullable<MonthlyReportData['activitySections']> {
  const sections: NonNullable<MonthlyReportData['activitySections']> = [];

  if (breakdown.google_ads) {
    const ads = breakdown.google_ads;
    sections.push({
      id: 'google_ads',
      title: 'Google Ads',
      items: [
        { label: 'Impressions', value: formatCount(ads.impressions) },
        { label: 'Clicks', value: formatCount(ads.clicks) },
        { label: 'Leads', value: formatCount(ads.leads) },
        { label: 'Ad spend', value: centsToUsd(ads.spend_cents) },
        { label: 'Attributed revenue', value: centsToUsd(ads.attributed_revenue_cents) },
      ],
    });
  }

  if (breakdown.seo) {
    const seo = breakdown.seo;
    sections.push({
      id: 'seo',
      title: 'SEO & Content',
      items: [
        { label: 'Articles created', value: formatCount(seo.articles_created) },
        { label: 'Articles published', value: formatCount(seo.articles_published) },
        { label: 'Keywords tracked', value: formatCount(seo.rankings_count) },
        { label: 'Average rank', value: seo.avg_rank != null ? String(seo.avg_rank) : '—' },
      ],
    });
  }

  if (breakdown.gbp) {
    const gbp = breakdown.gbp;
    sections.push({
      id: 'gbp',
      title: 'Google Business Profile',
      items: [
        { label: 'GBP posts published', value: formatCount(gbp.posts_created) },
        { label: 'Photos added', value: formatCount(gbp.photos_uploaded) },
        { label: 'Reviews responded to', value: formatCount(gbp.review_responses) },
        { label: 'Q&As handled', value: formatCount(gbp.qa_items) },
      ],
    });
  }

  if (breakdown.ai_receptionist) {
    const calls = breakdown.ai_receptionist;
    sections.push({
      id: 'ai_receptionist',
      title: 'AI Receptionist',
      items: [
        { label: 'Calls handled', value: formatCount(calls.calls) },
        { label: 'Total talk time', value: formatDuration(calls.total_duration_seconds) },
      ],
    });
  }

  return sections;
}

function buildSeoStats(seo: BeSeoBreakdown | undefined): MrSeoStat[] | undefined {
  if (!seo) return undefined;
  return [
    {
      key: 'articles',
      label: 'Articles Published',
      value: formatCount(seo.articles_published),
      note: `${formatCount(seo.articles_created)} created this month`,
    },
    {
      key: 'rankings',
      label: 'Keywords Tracked',
      value: formatCount(seo.rankings_count),
      note: seo.avg_rank != null ? `Average rank ${seo.avg_rank}` : 'Rankings monitored weekly',
    },
  ];
}

function wireChannelsHaveMetrics(channels: Array<Record<string, unknown>> | undefined): boolean {
  if (!channels?.length) return false;
  return channels.some((channel) => {
    const metrics = channel.metrics as unknown[] | undefined;
    const summary = str(channel.summary);
    return (metrics?.length ?? 0) > 0 && summary !== 'pending' && !summary.startsWith('$0.0');
  });
}

export function adaptMonthlyReport(be: BeMonthlyReport): MonthlyReportData {
  const row = be as Record<string, unknown>;
  const data: MonthlyReportData = {};
  const breakdown = row.channel_breakdown as BeChannelBreakdown | undefined;

  const heroFromBreakdown = buildHeroFromGoogleAds(breakdown?.google_ads);
  const wireHero = row.hero as Record<string, unknown> | undefined;
  if (heroFromBreakdown) {
    data.hero = heroFromBreakdown;
  } else if (hasHeroValues(wireHero)) {
    data.hero = {
      adSpend: num(wireHero?.ad_spend ?? wireHero?.adSpend),
      revenue: num(wireHero?.revenue),
      roi: num(wireHero?.roi),
      newPatients: num(wireHero?.new_patients ?? wireHero?.newPatients),
      lastMonth: {
        adSpend: num((wireHero?.last_month as Record<string, unknown> | undefined)?.ad_spend),
        revenue: num((wireHero?.last_month as Record<string, unknown> | undefined)?.revenue, 1),
        roi: num((wireHero?.last_month as Record<string, unknown> | undefined)?.roi, 1),
        newPatients: num((wireHero?.last_month as Record<string, unknown> | undefined)?.new_patients),
      },
    };
  }

  const currentMonth = row.current_month as Record<string, unknown> | undefined;
  if (currentMonth) {
    data.currentMonth = {
      label: str(currentMonth.label),
      status: mapMonthStatus(currentMonth.status as string | undefined),
      daysElapsed: num(currentMonth.days_elapsed ?? currentMonth.daysElapsed),
      daysTotal: num(currentMonth.days_total ?? currentMonth.daysTotal),
    };
  } else if (row.month) {
    data.currentMonth = {
      label: str(row.month),
      status: mapMonthStatus(row.status as string | undefined),
      daysElapsed: 0,
      daysTotal: 0,
    };
  }

  if (breakdown) {
    const channels = buildChannelsFromBreakdown(breakdown);
    if (channels.length > 0) data.channels = channels;

    const activitySections = buildActivitySections(breakdown);
    if (activitySections.length > 0) data.activitySections = activitySections;

    const seoStats = buildSeoStats(breakdown.seo);
    if (seoStats?.length) data.seoStats = seoStats;
  }

  const wireChannels = row.channels as Array<Record<string, unknown>> | undefined;
  if (!data.channels?.length && wireChannelsHaveMetrics(wireChannels)) {
    data.channels = wireChannels!.map((channelRow) => ({
      id: str(channelRow.id),
      name: str(channelRow.name),
      tone: (str(channelRow.tone, 'info') as MrChannel['tone']) ?? 'info',
      icon: (str(channelRow.icon, 'ads') as MrChannel['icon']) ?? 'ads',
      summary: str(channelRow.summary),
      metrics: ((channelRow.metrics as Array<Record<string, unknown>> | undefined) ?? []).map(
        (metric) => ({
          label: str(metric.label),
          value: str(metric.value),
          ...(metric.tone ? { tone: str(metric.tone) as MrChannel['tone'] } : {}),
        }),
      ),
      ...(channelRow.note ? { note: str(channelRow.note) } : {}),
    }));
  }

  const patientOutcomes = row.patient_outcomes as Record<string, unknown> | undefined;
  if (patientOutcomes && num((patientOutcomes.new_patients as Record<string, unknown> | undefined)?.value) > 0) {
    data.patientOutcomes = {
      newPatients: {
        value: num((patientOutcomes.new_patients as Record<string, unknown>)?.value),
        lastMonth: num((patientOutcomes.new_patients as Record<string, unknown>)?.last_month),
        change: num((patientOutcomes.new_patients as Record<string, unknown>)?.change),
      },
      totalAppointments: {
        value: num((patientOutcomes.total_appointments as Record<string, unknown> | undefined)?.value),
        lastMonth: num((patientOutcomes.total_appointments as Record<string, unknown> | undefined)?.last_month),
        change: num((patientOutcomes.total_appointments as Record<string, unknown> | undefined)?.change),
      },
      projectedLTV: {
        value: num((patientOutcomes.projected_ltv as Record<string, unknown> | undefined)?.value),
        lastMonth: num((patientOutcomes.projected_ltv as Record<string, unknown> | undefined)?.last_month),
        change: num((patientOutcomes.projected_ltv as Record<string, unknown> | undefined)?.change),
      },
      revenueByService: [],
    };
  }

  const warnings = row.warnings as Array<Record<string, unknown>> | undefined;
  const alerts = row.alerts as Array<Record<string, unknown>> | undefined;
  const alertRows = [...(alerts ?? []), ...(warnings ?? [])];
  if (alertRows.length > 0) {
    data.alerts = alertRows.map((alertRow) => ({
      type: 'warning' as const,
      title: str(alertRow.title ?? alertRow.message, 'Notice'),
      detail: str(alertRow.detail ?? alertRow.description, '—'),
    }));
  }

  if (row.total_actions != null) {
    data.totalActions = formatCount(row.total_actions as number);
  }

  return data;
}

export async function fetchMonthlyReportMonths(): Promise<MonthlyReportMonth[]> {
  const be = await growthGet<
    | { months?: Array<{ label?: string; value?: string; status?: string }> }
    | Array<{ label?: string; value?: string; status?: string }>
  >('/monthly-report/months/');
  const rows = Array.isArray(be) ? be : be.months ?? [];
  return rows.map((month) => ({
    label: str(month.label),
    value: str(month.value),
    status: mapMonthStatus(month.status),
  }));
}

export async function fetchMonthlyReport(month?: string): Promise<MonthlyReportFetchResult> {
  const params: Record<string, string> = {};
  if (month) params.month = month;

  try {
    const { data: be, status } = await growthGetAccepted<BeMonthlyReport & MarketingJobData>(
      '/monthly-report/',
      params,
    );

    const row = be as Record<string, unknown>;
    const reportId = str(row.report_id) || undefined;
    const jobId = str(row.job_id) || undefined;

    if (status === 202) {
      return {
        data: adaptMonthlyReport(be),
        generating: true,
        ...(reportId ? { reportId } : {}),
        ...(jobId ? { jobId } : {}),
      };
    }

    const wireStatus = str(row.status).toLowerCase();
    return {
      data: adaptMonthlyReport(be),
      generating: wireStatus === 'generating',
      ...(reportId ? { reportId } : {}),
      ...(jobId ? { jobId } : {}),
    };
  } catch {
    return {
      data: adaptMonthlyReport({}),
      generating: false,
    };
  }
}

export async function exportMonthlyReportPdf(reportId: string): Promise<void> {
  const encodedId = encodeURIComponent(reportId);
  try {
    const { blob, status } = await growthPostBlob(`/monthly-report/${encodedId}/export-pdf/`);
    if (status === 200 && blob.size > 0) {
      downloadBlob(blob, `monthly-report-${reportId}.pdf`);
      return;
    }
  } catch {
    // Backend may reject encoded ids — fall through to async job response.
  }

  await growthPostAsync(`/monthly-report/${encodedId}/export-pdf/`);
}
